import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

export default function VoiceButton({ onTranscript, disabled }) {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const holdingRef = useRef(false);
  const secsRef = useRef(0);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && pickMime() !== null;

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach(t => t.stop()); }, []);

  const start = async (e) => {
    e.preventDefault();
    if (disabled || state !== "idle") return;
    holdingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!holdingRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
        if (secsRef.current < 1 || blob.size < 500) { setState("idle"); setSecs(0); toast("Hold the mic a bit longer and talk."); return; }
        setState("sending");
        try {
          const fd = new FormData();
          fd.append("audio", blob, `voice.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
          const res = await fetch(`${API}/coach/transcribe`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("ihmj_token")}` }, body: fd });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.detail || "Couldn't hear that.");
          onTranscript(data.text);
        } catch (err) {
          toast.error(err.message || "Couldn't hear that. Try again.");
        } finally { setState("idle"); setSecs(0); }
      };
      recRef.current = rec;
      rec.start(250);
      secsRef.current = 0;
      setSecs(0);
      setState("recording");
      timerRef.current = setInterval(() => { secsRef.current += 1; setSecs(secsRef.current); if (secsRef.current >= 90) stop(); }, 1000);
    } catch {
      holdingRef.current = false;
      toast.error("Microphone blocked. Allow mic access in your browser to talk to your coach.");
    }
  };

  const stop = () => {
    holdingRef.current = false;
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  if (!supported) return null;
  const recording = state === "recording";

  return (
    <div className="relative shrink-0">
      {recording && (
        <div className="absolute -top-12 right-0 whitespace-nowrap bg-[#FD0F10] text-white text-xs font-mono-ui uppercase tracking-wider px-3 py-1.5 rounded shadow-lg flex items-center gap-2" data-testid="voice-recording-indicator">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Listening {secs}s. Release to send.
        </div>
      )}
      <button
        type="button"
        disabled={disabled || state === "sending"}
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="Hold to talk to your coach"
        title="Hold to talk"
        style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
        className={`h-full min-h-[46px] w-12 rounded-lg border flex items-center justify-center transition-colors disabled:opacity-50 select-none ${recording ? "bg-[#FD0F10] border-[#FD0F10] text-white scale-105" : "border-[#2A2E39] text-gray-300 hover:border-[#FD0F10] hover:text-white"}`}
        data-testid="coach-voice-btn"
      >
        {state === "sending" ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-testid="voice-transcribing" /> : recording ? <Square size={16} fill="currentColor" /> : <Mic size={18} />}
      </button>
    </div>
  );
}
